/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import deepcopy from 'deepcopy';
import isArray from 'lodash.isarray';
import {error} from '@ion/core/src/logger/logger';
import {loadDeviceType} from '@ion/core/src/services/Connect';
import {Asset, Assets, Device, Devices} from '@ion/core/src/types/entities';
import {DeviceSearchableProperty} from '@ion/core/src/types/services';
import {isArrayDeepEmpty} from '@ion/core/src/utils/ArrayUtil';
import assetLoader from '@ion/dls/src/selectiontree/utils/AssetTreeLoader';
import DeviceLoader from '@ion/dls/src/selectiontree/utils/DeviceLoader';
import {injectAsset, removeDevicesFromTree, setAssetsLoading} from '@ion/dls/src/selectiontree/utils/TreeViewUtils';
import {ReportTabMode} from '@/types/redux';
import {getRootNode, removeParentKey} from '../helpers';
import {APPLY_DEVICES_FILTER} from './reports';
import {SET_DEVICES_FILTER_RESPONSE} from './tracking';

export const SET_FILTER_TREE_NODES = 'SET_FILTER_TREE_NODES';
export const RESET_FILTER_TREE_NODES = 'RESET_FILTER_TREE_NODES';

export const SET_GROUPS_NODE_KEY_MAP = 'SET_GROUPS_NODE_KEY_MAP';

export const SET_GROUP_FILTERS_EXPANDED = 'SET_GROUP_FILTERS_EXPANDED';
export const SET_GROUP_FILTERS_CHECKED = 'SET_GROUP_FILTERS_CHECKED';
export const UPDATE_GROUP_FILTERS_CHECKED = 'UPDATE_GROUP_FILTERS_CHECKED';

export const ASSETS_LOADING          = 'ASSETS_LOADING';
export const ASSET_LOADING_COMPLETED = 'ASSET_LOADING_COMPLETED';

export const INSERT_ASSETS    = 'INSERT_ASSETS';
export const RESET_TREE_NODES = 'RESET_TREE_NODES';
export const UPDATE_ITEM_STORED = 'UPDATE_ITEM_STORED';

export const SET_INCLUDE_SC_DEVICES = 'SET_INCLUDE_SC_DEVICES';

export const SET_SC_DEVICES_TYPES = 'SET_SC_DEVICES_TYPES';

const SC_DEVICES = 'SC1004, SC1004V, SC1204, SC1204V, SC1204S, SC1205V, SC1102, SC1302';

export interface SetFilterTreeNodesType {
  nodes: any;
  mode?: ReportTabMode;
}
export interface keysUpdateType {
  keys: string[];
  type: string;
  mode?: ReportTabMode;
}

export interface ResetTreeNodesType {
  mode?: ReportTabMode;
}

interface GenerateResultsType {
  nodes: any;
  assetKeyMap: any[];
  assets: Assets | Devices;
  groupId: number;
  completed: boolean;
  remaining: number;
  dispatch: any
}

interface PreverifyLoadGroupAssets {
  nodes: any;
  groupId: number;
  groupKey: string;
  dispatch: any
  filter?: string
}

interface LoadAssetsResponse {
  assets?: Asset[];
  devices?: Device[];
  remaining?: number;
  completed?: boolean;
};

/**
 * Defines a utility to load assets and update loader state
 */
export const loadGroupAssets = (groupId: number, groupKey: string) => {
  return async(dispatch, getState) => {
    const {
      groupsFilter: {
        treeNodes,
        assetKeyMap,
        includeSCDevices,
        SCDeviceTypes
      }
    } = getState();
    const nodes = deepcopy(treeNodes);
    const {
      assets,
      completed: assetsCompleted,
      remaining: assetsRemaining
    } = await preverifyLoadGroupAssets({nodes, groupId, groupKey, dispatch});
    generateResults({
      nodes,
      groupId,
      assets,
      assetKeyMap,
      remaining : assetsRemaining,
      completed : assetsCompleted,
      dispatch
    });

    if (includeSCDevices) {
      const {
        devices,
        completed: devicesCompleted,
        remaining: devicesRemaining
      } = await preverifyLoadDevices({nodes, groupId, groupKey, dispatch, filter: SCDeviceTypes});
      // verify if either assets or devices loading state is incomplete.
      const completed = !!assetsCompleted && !!devicesCompleted;
      // verify if either assets or devices loading remaining is left. If assetsRemaining is 0, check
      // if devices remaining is 0. If both are 0, it will be 0
      //
      const remaining = assetsRemaining !== 0 ? assetsRemaining : devicesRemaining;
      generateResults({nodes, assets: devices, groupId, assetKeyMap, remaining, completed, dispatch});
    };
  };
};

/**
 * Defines a utility to load assets and update loader state
 */
const preverifyLoadGroupAssets = async({
  nodes,
  groupId,
  groupKey,
  dispatch
}: PreverifyLoadGroupAssets): Promise<LoadAssetsResponse> => {
  const rootNode = isArray(nodes) ? nodes : [nodes];
  setAssetsLoading({rootNode: rootNode?.[0], groupId, isLoading: true});
  dispatch({type: ASSETS_LOADING, treeNodes: Object.freeze(nodes)});
  try {
    const results = await assetLoader.loadAssets(groupId, groupKey);
    return results;
  } catch (err) {
    error(err);
    return {};
  }
};

/**
 * Defines a utility to load assets and update loader state
 */
const preverifyLoadDevices = async({
  nodes,
  groupId,
  groupKey,
  dispatch,
  filter
}: PreverifyLoadGroupAssets): Promise<LoadAssetsResponse> => {
  const rootNode = getRootNode(nodes);
  setAssetsLoading({rootNode: rootNode?.[0], groupId, isLoading: true});
  dispatch({type: ASSETS_LOADING, treeNodes: Object.freeze(nodes)});
  try {
    return await DeviceLoader.loadDevices(groupId, groupKey, {
      [DeviceSearchableProperty.type] : filter
    });
  } catch (err) {
    error(err);
    return {};
  }
};

/**
 * Defines a utility to genereate result and insert assets
 */
const generateResults = ({
  nodes,
  groupId,
  assets,
  assetKeyMap,
  remaining,
  completed,
  dispatch
}: GenerateResultsType) => {
  const items = assets?.filter(({id}) => !assetKeyMap[id]);
  const rootNode = getRootNode(nodes);
  const {insertedKeys} = injectAsset({treeNode: rootNode?.[0], assets: items, groupId, completed, remaining});
  setAssetsLoading({rootNode: rootNode?.[0], groupId, remaining, completed});
  dispatch({type: INSERT_ASSETS, treeNodes: Object.freeze(nodes), insertedKeys});
  return true;
};

/**
 * Defines a utility to reset assets state
 */
export const resetFilterTreeNodes = ({nodes = null}: any) => ({type: RESET_TREE_NODES, nodes});

/**
 * @function setFilterTreeNodes
 * Action to set the tree nodes
 * @param nodes nodes to update
 * @param mode enum to show if the update is for Shipment History, FM History or Tracking
 */
export const setFilterTreeNodes = ({nodes, mode}: SetFilterTreeNodesType) => {
  return {type: SET_FILTER_TREE_NODES, nodes, mode};
};

/**
 * @function resetTreeNodes
 * Action to reset the tree nodes
 * @param mode enum to show if the update is for Shipment History, FM History or Tracking
 */
export const resetTreeNodes = ({mode}: ResetTreeNodesType) => {
  return {type: RESET_FILTER_TREE_NODES, mode};
};

/**
 * @function updateTreeKeys
 * Action to reset the tree nodes
 * @param keys keys that need to be expanded or checked
 * @param type indicates the action type
 * @param mode enum to show if the update is for Shipment History, FM History or Tracking
 */
export const updateTreeKeys = ({keys, type, mode}: keysUpdateType) => {
  if (type === SET_GROUP_FILTERS_CHECKED && !isArrayDeepEmpty(keys)) {
    keys = removeParentKey(keys);
  }
  return {type: type, keys, mode};
};

export const updateItemStored = (itemInfo) => ({
  type : UPDATE_ITEM_STORED, itemInfo
});

/**
 * @private
 */
const removeDevices = ({nodes, deviceGroupIds, assetGroupIds, assetsStatus}) => {
  return async(dispatch) => {
    const {treeNodes} = removeDevicesFromTree(nodes, deviceGroupIds);
    assetGroupIds.forEach(groupId => {
      const {completed, remaining} = assetsStatus[groupId];
      setAssetsLoading({
        groupId  : Number(groupId),
        rootNode : nodes,
        remaining,
        completed
      });
      dispatch({type: SET_FILTER_TREE_NODES, nodes: Object.freeze(nodes)});
    });
  };
};

const getDevicesRequestCompleted = (devicesStatus, assetsStatus, groupId) => {
  if (assetsStatus?.[groupId]) {
    return (
      devicesStatus[groupId].completed === true &&
      assetsStatus[groupId].completed === true
    );
  }
  return false;
};

/**
 * @private
 */
const addDevices = ({
  nodes,
  cachedDevices,
  deviceGroupIds,
  assetGroupIds,
  devicesStatus,
  assetsStatus
}) => {
  return async(dispatch) => {
    if (cachedDevices.length) {
      deviceGroupIds.forEach((id) => {
        // if either asset of device for the groupId is incomplete, complete status should be false
        const groupId   = Number(id);
        const completed = getDevicesRequestCompleted(devicesStatus, assetsStatus, id);
        const remaining = devicesStatus[id]?.remaining || 100;
        const {insertedKeys, root} = injectAsset({
          treeNode : nodes,
          assets   : cachedDevices,
          completed,
          remaining,
          groupId
        });
        setAssetsLoading({
          rootNode : nodes,
          remaining,
          completed,
          groupId
        });
        dispatch({type: INSERT_ASSETS, treeNodes: [Object.freeze(root)], insertedKeys});
      });
    } else {
      // loop through group Id of asset from the cached list, update the tree to show the loader
      assetGroupIds.forEach(groupId => {
        setAssetsLoading({
          rootNode  : nodes,
          groupId   : Number(groupId),
          remaining : 100,
          completed : false
        });
      });
      dispatch({type: SET_FILTER_TREE_NODES, nodes: Object.freeze(nodes)});
    };
  };
};

/**
 * setIncludeSCDevices update the includeSCDevices state so that
 * request to CTC can refer to this state to determine requesting for SC devices
 * is required.
 *
 * ```typescript
 *
 * import {setIncludeSCDevices} from '../../store/actions/groups-filter';
 *
 * dispatch(setIncludeSCDevices(true));
 *
 * ```
 *
 */
export const setIncludeSCDevices = (state: boolean, reportType) => {
  return async(dispatch, getState) => {
    const {
      groupsFilter: {
        treeNodes: initialTree,
        assetKeyMap,
        includeSCDevices
      }
    } = getState();

    const assetsStatus = assetLoader.getStatus();
    const devicesStatus = DeviceLoader.getStatus();
    const cachedDevices = Object.values(assetKeyMap)
      .filter(({isDevice}) => isDevice)
      .map(({id, accountId, title: name}) => ({id, accountId, name}));
    const cachedDeviceIds = cachedDevices.map(({accountId}) => accountId);
    const assetGroupIds = Object.keys(assetsStatus);
    // if device was only added by device search, device status got never updated,
    // therefore we have to combine both from cached devices and device status.
    const deviceGroupIds = Array.from(new Set([...Object.keys(devicesStatus), ...cachedDeviceIds]));
    const nodes = isArray(initialTree) ? initialTree[0] : initialTree;
    // when unchecked, we need to remove all devices loaded on the tree
    if (!state) {
      dispatch(removeDevices({nodes, deviceGroupIds, assetGroupIds, assetsStatus}));
      // if including SC devices are disabled, it should remove all selected devices
      // from the redux filter state in both trackingMap and SHR
      dispatch({type: SET_DEVICES_FILTER_RESPONSE, value: []});
      dispatch({
        type     : APPLY_DEVICES_FILTER,
        selected : [],
        reportType
      });
    };
    if (state) {
      cachedDevices.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      !includeSCDevices && dispatch(addDevices({
        nodes,
        cachedDevices,
        deviceGroupIds,
        assetGroupIds,
        devicesStatus,
        assetsStatus
      }));
    };
    dispatch({type: SET_INCLUDE_SC_DEVICES, state});
  };
};

export const setSCDeviceTypes = () => {
  return async(dispatch) => {
    const params = {
      pageSize : 100,
      filterOn : {
        name : SC_DEVICES
      },
      sortOn : '+name'
    };
    const {items} = await loadDeviceType(params);
    const deviceIds = items.reduce((prev, {id}) => `${prev}${id},`, '');
    dispatch({type: SET_SC_DEVICES_TYPES, deviceIds});
  };
};
